import { Component } from '@theme/component';

/**
 * Switches between merchant-configured collection panels.
 *
 * @typedef {Object} Refs
 * @property {HTMLButtonElement[]} [tabs]
 * @property {HTMLElement[]} [panels]
 * @property {HTMLAnchorElement[]} [actions]
 *
 * @extends {Component<Refs>}
 */
class EclerCollectionTabs extends Component {
  connectedCallback() {
    super.connectedCallback();

    const selectedTab = this.refs.tabs?.find((tab) => tab.getAttribute('aria-selected') === 'true');
    if (selectedTab) this.#setActiveTab(selectedTab);
  }

  /**
   * Selects the collection associated with the activated tab.
   * @param {MouseEvent} event
   */
  select(event) {
    if (!(event.target instanceof HTMLButtonElement)) return;

    this.#setActiveTab(event.target);
  }

  /**
   * Supports the standard horizontal tab keyboard pattern.
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (!(event.target instanceof HTMLButtonElement)) return;

    const tabs = this.refs.tabs || [];
    const currentIndex = tabs.indexOf(event.target);
    if (currentIndex < 0) return;

    let nextIndex;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabs.length - 1;
    if (nextIndex === undefined) return;

    event.preventDefault();
    const nextTab = tabs[nextIndex];
    nextTab.focus();
    this.#setActiveTab(nextTab);
  }

  /** @param {HTMLButtonElement} selectedTab */
  #setActiveTab(selectedTab) {
    const targetId = selectedTab.dataset.collectionTab;
    if (!targetId) return;

    for (const tab of this.refs.tabs || []) {
      const isSelected = tab === selectedTab;
      tab.classList.toggle('is-active', isSelected);
      tab.setAttribute('aria-selected', isSelected.toString());
      tab.tabIndex = isSelected ? 0 : -1;
    }

    for (const panel of this.refs.panels || []) {
      const isSelected = panel.dataset.collectionPanel === targetId;
      panel.classList.toggle('is-active', isSelected);
      panel.hidden = !isSelected;
    }

    for (const action of this.refs.actions || []) {
      const isSelected = action.dataset.collectionAction === targetId;
      action.classList.toggle('is-active', isSelected);
      action.hidden = !isSelected;
    }
  }
}

if (!customElements.get('ecler-collection-tabs-component')) {
  customElements.define('ecler-collection-tabs-component', EclerCollectionTabs);
}
